/**
 * Chemical Stepper Helpers
 * Handles add stock for chemicals (raw materials) with dual unit/total quantities.
 * raw_materials_transactions uses unit_quantity + total_quantity (not quantity).
 * raw_materials_inventory_items uses current_unit_quantity + current_total_quantity.
 */

import { supabase } from '../../../../../supabaseClient';

const BUCKET_MAP = {
  invoice: 'raw-materials-transactions-invoices',
  po:      'raw-material-inventory-po',
  coa:     'raw-materials-inventory-coa',
  msds:    'raw-materials-inventory-msds',
  bol:     'raw-material-inventory-bol'
};

async function uploadChemicalDocuments(companyId, documents) {
  const uploads = {};
  const timestamp = Date.now();

  for (const [key, bucket] of Object.entries(BUCKET_MAP)) {
    const file = documents?.[key];
    if (!file) continue;
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${companyId}/${timestamp}_${safeName}`;
      const { error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (!error) {
        uploads[key] = { path: filePath, name: file.name, size: file.size };
      } else {
        console.error(`Upload error for ${key}:`, error);
      }
    } catch (err) {
      console.error(`Upload exception for ${key}:`, err);
    }
  }
  return uploads;
}

/**
 * Add stock for a chemical (raw material).
 * quantity = number of units (drums/bags/etc.)
 * total_quantity = quantity * item.primary_unit_quantity (e.g., kg per drum)
 */
export async function submitAddStockChemical(companyId, inventoryType, config, data) {
  try {
    const {
      item, location, quantity,
      lotNumber, batchNumber,
      vendor, invoiceNumber, invoiceDate,
      poNumber, poDate, unitCost,
      expiryDate,
      documents, notes
    } = data;

    if (!item || !location || !quantity || quantity <= 0) {
      throw new Error('Item, location, and positive quantity are required');
    }
    if (!lotNumber || !batchNumber) {
      throw new Error('Lot number and batch number are required');
    }

    const unitSize = parseFloat(item.primary_unit_quantity) || 1;
    let unitQty, totalQty;
    if (data.quantityMode === 'measure') {
      // quantity is in base measure (kg, gal, etc.) — derive container count
      const measureQty = parseFloat(quantity);
      unitQty  = parseFloat((measureQty / unitSize).toFixed(4));
      totalQty = parseFloat(measureQty.toFixed(4));
    } else {
      // quantity is in containers (drums, bags, etc.) — default behaviour
      unitQty  = parseFloat(quantity);
      totalQty = parseFloat((unitQty * unitSize).toFixed(4));
    }
    const finalCost = unitCost ? parseFloat(unitCost) : (item.unit_cost || null);

    // Upload documents
    const docUploads = await uploadChemicalDocuments(companyId, documents || {});

    // Build transaction record
    const transactionData = {
      company_id:       companyId,
      raw_material_id:  item.id,
      location_id:      location.id,
      transaction_type: 'inventory_creation',
      unit_quantity:    unitQty,
      total_quantity:   totalQty,
      lot_number:       lotNumber || null,
      batch_number:     batchNumber || null,
      supplier_id:      vendor?.id || null,
      invoice_number:   invoiceNumber || null,
      invoice_date:     invoiceDate || null,
      po_number:        poNumber || null,
      po_date:          poDate || null,
      unit_cost:        finalCost,
      transaction_date: new Date().toISOString(),
      notes:            notes || null
    };

    if (docUploads.invoice) transactionData.invoice_file_path = docUploads.invoice.path;
    if (docUploads.po)      transactionData.po_file_path      = docUploads.po.path;
    if (docUploads.coa)     transactionData.coa_file_path     = docUploads.coa.path;
    if (docUploads.msds)    transactionData.msds_file_path    = docUploads.msds.path;
    if (docUploads.bol)     transactionData.bol_file_path     = docUploads.bol.path;

    // Insert transaction
    const { data: tx, error: txError } = await supabase
      .from('raw_materials_transactions')
      .insert([transactionData])
      .select()
      .single();

    if (txError) throw txError;

    // Delete any inactive ghost records (same lot/batch) to prevent reactivation
    await supabase
      .from('raw_materials_inventory_items')
      .delete()
      .eq('company_id', companyId)
      .eq('raw_material_id', item.id)
      .eq('location_id', location.id)
      .eq('lot_number', lotNumber || '')
      .eq('batch_number', batchNumber || '')
      .eq('is_active', false);

    // Check for existing active item with same lot/batch
    const { data: existingItem, error: existError } = await supabase
      .from('raw_materials_inventory_items')
      .select('*')
      .eq('company_id', companyId)
      .eq('raw_material_id', item.id)
      .eq('location_id', location.id)
      .eq('lot_number', lotNumber || '')
      .eq('batch_number', batchNumber || '')
      .eq('is_active', true)
      .maybeSingle();

    if (existError && existError.code !== 'PGRST116') throw existError;

    if (existingItem) {
      // Merge into existing active lot
      const { error: updateError } = await supabase
        .from('raw_materials_inventory_items')
        .update({
          current_unit_quantity:  existingItem.current_unit_quantity + unitQty,
          current_total_quantity: parseFloat((existingItem.current_total_quantity + totalQty).toFixed(4)),
          source_transaction_id:  tx.id,
          is_active:              true
        })
        .eq('id', existingItem.id);
      if (updateError) throw updateError;
    } else {
      // Create new item record
      const { error: createError } = await supabase
        .from('raw_materials_inventory_items')
        .insert([{
          company_id:              companyId,
          raw_material_id:         item.id,
          location_id:             location.id,
          lot_number:              lotNumber || '',
          batch_number:            batchNumber || '',
          current_unit_quantity:   unitQty,
          original_unit_quantity:  unitQty,
          current_total_quantity:  totalQty,
          original_total_quantity: totalQty,
          source_transaction_id:   tx.id,
          expiry_date:             expiryDate || null,
          is_active:               true,
          created_at:              new Date().toISOString()
        }]);
      if (createError) throw createError;
    }

    // Recalculate aggregate inventory
    await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
      p_company_id:      companyId,
      p_raw_material_id: item.id,
      p_location_id:     location.id
    });

    return { success: true, message: 'Chemical stock added successfully' };
  } catch (error) {
    console.error('Error adding chemical stock:', error);
    return { success: false, message: error.message };
  }
}
